/**
 * Users tab — the member roster: Email (+ login-method icon) · First · Last · Role · License · Last
 * login. Role maps the Zoom role id (0/1/2 → Owner/Admin/Member; anything else = a Custom role whose
 * name isn't in the mirror). The License cell STACKS the base meeting license (Basic/Licensed → plan)
 * with each per-user add-on the mirror carries (Zoom Phone / Webinar (+cap) / Large meeting (+cap)) on
 * its own line, Zoom-portal style — a null flag simply omits the line, never asserts the add-on is off, and
 * a flag with no live line of that add-on on the account (API *_has_line) omits it too. The PHONE chip,
 * when the account has phone data (API `phone_plans` is an array), is instead the user's Zoom calling
 * plans by name, Workplace-bundle plans removed (owner ruling 2026-09-18).
 * (zoom_events is intentionally not shown per the audit low-signal caveat.) Keeps the client filter, the
 * refresh-users Sync-now, and the member deep-link highlight (?member=<email>).
 *
 * Two sub-tabs, like Zoom (N0-6, owner ruling 2026-09-18): Users (n) — active members; n = the API's
 * roster count, invitees excluded — | Pending (n) — invitees who have not accepted yet: Email · License ·
 * Invited ("Currently in" removed, owner ruling 2026-09-19). Both sub-tabs carry the SAME search box
 * (RosterSearch; Pending filters by email). A ?member= deep link to a pending email opens Pending. Until
 * the pending list is LOADED for the roster's day the tab reads "Pending" (no number) and says so.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/api/client";
import { LoginTypeIcons } from "@/components/portal/LoginTypeIcon";
import {
  Input,
  Table,
  type Column,
  ErrorState,
  EmptyState,
  SkeletonRows,
  Badge,
  Tabs,
  TabPanel,
} from "@/components/ui";
import { formatInstant, formatInt } from "@/lib/format";
import { planName } from "@/lib/planNames";
import { cn } from "@/lib/cn";
import type { CustomerPendingUser, CustomerUser } from "@/api/types";
import type { TKey } from "@/i18n/keys";
import { Section, FeedNote } from "../display";
import { SyncNowControl } from "../SyncNowControl";
import { useCustomerUsers, useRefreshAfterSync } from "../useCustomer";

// Deliberately NOT `ReturnType<typeof useTranslation>["t"]` — see the identical note in
// SyncNowControl.tsx: that exact overloaded generic type gets expensive enough against a large resource
// tree that adding the Pool module's ~150 keys pushed this file's t() usage over tsc's "type
// instantiation is excessively deep" limit. Every call below passes a real key with no interpolation
// options, so this narrower structural alias is sufficient.
type TFn = (key: TKey) => string;

/** Zoom role id → label. 0/1/2 are the system roles; any other id is a custom role (name not in data). */
function roleLabel(t: TFn, roleId: string | null): string {
  switch (roleId) {
    case "0":
      return t("customer.users.roleOwner");
    case "1":
      return t("customer.users.roleAdmin");
    case "2":
      return t("customer.users.roleMember");
    case null:
    case "":
      return "—";
    default:
      return t("customer.users.roleCustom");
  }
}

/** Zoom role id → a fixed display order: Owner, Admin, Custom, Member (custom = any non-0/1/2 id). */
function roleRank(roleId: string | null): number {
  switch (roleId) {
    case "0":
      return 0; // Owner
    case "1":
      return 1; // Admin
    case "2":
      return 3; // Member (last)
    default:
      return 2; // Custom (a non-system role id, or null)
  }
}

/** Base-meeting-license facets (user_type). Only these three are offered as filter chips; a null/other
 *  user_type is "other" and is never chip-able (it just fails a base filter, never invents a value). */
const BASE_FACETS = ["basic", "licensed", "none"] as const;
type BaseFacet = (typeof BASE_FACETS)[number] | "other";
function baseFacet(user: CustomerUser): BaseFacet {
  switch (user.user_type) {
    case 1:
      return "basic";
    case 2:
      return "licensed";
    case 4:
      return "none";
    default:
      return "other";
  }
}

/** Per-user add-on facets, in display order. */
const ADDON_FACETS = ["phone", "webinar", "large_meeting"] as const;
type AddonFacet = (typeof ADDON_FACETS)[number];
const ADDON_LABEL_KEY = {
  phone: "customer.users.licensePhone",
  webinar: "customer.users.licenseWebinar",
  large_meeting: "customer.users.licenseLargeMeeting",
} as const satisfies Record<AddonFacet, TKey>;

/**
 * The user's phone chips, as Zoom plan names (null = the generic "Zoom Phone" label).
 * AUTHORITATIVE (owner ruling 2026-09-18): when the API sends `phone_plans` as an array, the account has
 * phone data and the chips are exactly those calling plans (the API already dropped Workplace-bundle plans
 * and decided phone_not_enabled / no-plan / not-a-phone-user → []). FALLBACK: null/absent = no phone data
 * today, or this user's plans are unknown → the pre-ruling gate (flag && live line && !bundled), one
 * generic chip.
 */
function phoneChips(user: CustomerUser): (string | null)[] {
  if (Array.isArray(user.phone_plans)) {
    const names: (string | null)[] = [];
    for (const p of user.phone_plans) if (!names.includes(p.name)) names.push(p.name);
    return names;
  }
  return user.has_phone && user.phone_bundled !== true && user.phone_has_line === true
    ? [null]
    : [];
}

/**
 * The add-ons a user actually SHOWS as an à-la-carte seat. BUNDLED-AWARENESS: an add-on folded into the
 * account tier (*_bundled === true) is HIDDEN. FAIL-SAFE: only an explicit true suppresses; null/false/
 * undefined (incl. an unknown tier, which carries *_bundled=false) still shows — never hide a real seat.
 * LINE GATE (N0-1): the per-user flag is a Zoom user SETTING that outlives the add-on (Pathumwan: 7 users
 * flagged "Large Meeting 500" on an account that never held a Large Meeting line), so a chip also needs
 * the API's *_has_line === true — the account holds a live line of it. The API owns that rule; this only
 * reads it, and a missing key hides the chip (nothing proves the add-on).
 * This is the SINGLE predicate shared by the License cell and the License filter, so a bundled add-on
 * that shows no chip can never be matched by its own filter (the parity trap). Phone: see phoneChips.
 */
function shownAddons(user: CustomerUser): AddonFacet[] {
  const out: AddonFacet[] = [];
  if (phoneChips(user).length > 0) out.push("phone");
  if (user.webinar && user.webinar_bundled !== true && user.webinar_has_line === true)
    out.push("webinar");
  if (
    user.large_meeting &&
    user.large_meeting_bundled !== true &&
    user.large_meeting_has_line === true
  )
    out.push("large_meeting");
  return out;
}

/**
 * The license "stack" shown in ONE cell, Zoom-portal style: the base meeting license on the first line,
 * then each shown add-on (Zoom Phone / Webinar (+cap) / Large meeting (+cap)) on its own line.
 * zoom_events is intentionally omitted (audit low-signal caveat).
 */
function licenseLines(t: TFn, user: CustomerUser, baseType: string | null): string[] {
  const base =
    user.user_type === 1
      ? t("customer.users.licenseBasic")
      : user.user_type === 2
        ? planName(baseType)
        : user.user_type === 4
          ? t("customer.users.licenseNone")
          : "—";
  const capacity: Record<AddonFacet, number | null> = {
    phone: null,
    webinar: user.webinar_capacity,
    large_meeting: user.large_meeting_capacity,
  };
  const lines = [base];
  for (const a of shownAddons(user)) {
    if (a === "phone") {
      for (const name of phoneChips(user)) lines.push(name ?? t(ADDON_LABEL_KEY.phone));
      continue;
    }
    const cap = capacity[a];
    lines.push(cap ? `${t(ADDON_LABEL_KEY[a])} ${formatInt(cap)}` : t(ADDON_LABEL_KEY[a]));
  }
  return lines;
}

/** Immutable toggle: return a new Set with `value` added or removed (state identity changes → re-render). */
function toggleSet<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/** A pill-shaped toggle for the License facet filter (no shared multi-select primitive exists). */
function FilterChip({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onToggle}
      className={cn(
        "rounded-full px-2.5 py-0.5 text-(length:--text-2xs) font-medium ring-1 ring-inset transition-colors",
        active
          ? "bg-(--brand-subtle) text-(--brand-strong) ring-(--brand)"
          : "bg-(--surface-sunken) text-(--text-muted) ring-(--border) hover:text-(--text)",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--brand)",
      )}
    >
      {label}
    </button>
  );
}

/** An invite's licence (user_type): 1 = Basic, 2 = Licensed; anything else is not asserted. */
function pendingLicense(t: TFn, userType: number | null): string {
  return userType === 1
    ? t("customer.users.typeBasic")
    : userType === 2
      ? t("customer.users.typeLicensed")
      : "—";
}

type SubTab = "users" | "pending";

/** The roster search box — ONE component for both sub-tabs (same placement, look and behaviour). */
function RosterSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="relative w-full max-w-sm">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--text-subtle)"
          aria-hidden
        />
        <Input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          autoComplete="off"
          spellCheck={false}
          className="h-9 pl-9"
        />
      </div>
      {/* The row count moved to the Table's own bottom bar ("N result(s)"), per the pagination
          redesign — no separate top count line. */}
    </div>
  );
}

// Login-method glyphs (Google/Microsoft/Apple/Facebook/email/SSO/phone) live in the shared
// LoginTypeIcon component so the Zoom-style brand marks are reusable + testable.

export function UsersTab({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const deepLinkedEmail = params.get("member");

  const users = useCustomerUsers(accountId, true);
  const { refreshUsers } = useRefreshAfterSync(accountId);

  // LOADED or not: until the portal holds the pending list for this account's roster day (before
  // ZPCD's first pending partition, or while a day's attach lags) the API sends pending_loaded=false
  // and pending_count=null. That is "not loaded yet" — never "Pending (0)" / the empty state. An older
  // API without the flag: loaded iff it sent a pending list at all.
  const pendingLoaded =
    users.data?.pending_loaded ?? (users.data !== undefined && users.data.pending !== undefined);
  const pending = useMemo(
    () => (pendingLoaded ? (users.data?.pending ?? []) : []),
    [users.data, pendingLoaded],
  );
  const pendingCount = users.data?.pending_count ?? pending.length;
  const pendingTabLabel = pendingLoaded
    ? t("customer.users.tabPending", { count: pendingCount })
    : t("customer.users.tabPendingNotLoaded");
  // A ?member= deep link to an email that is PENDING here opens the Pending sub-tab (case-insensitive:
  // search hands over Zoom's email verbatim, the invite may differ in case).
  const deepLinkedPending =
    !!deepLinkedEmail &&
    pending.some((p) => p.email.toLowerCase() === deepLinkedEmail.toLowerCase());
  const [sub, setSub] = useState<SubTab>("users");
  useEffect(() => {
    if (deepLinkedPending) setSub("pending");
  }, [deepLinkedPending]);

  const [filter, setFilter] = useState(deepLinkedEmail ?? "");
  // The Pending sub-tab's own query (by email), seeded from a deep link exactly like the Users one.
  const [pendingFilter, setPendingFilter] = useState(deepLinkedEmail ?? "");
  useEffect(() => {
    if (deepLinkedEmail) {
      setFilter(deepLinkedEmail);
      setPendingFilter(deepLinkedEmail);
    }
  }, [deepLinkedEmail]);
  const pendingShown = useMemo(() => {
    const q = pendingFilter.trim().toLowerCase();
    return q ? pending.filter((p) => p.email.toLowerCase().includes(q)) : pending;
  }, [pending, pendingFilter]);

  // License-type facet filter. OR within each group (bases are mutually exclusive; add-ons additive),
  // AND across the two groups — e.g. "Licensed" + "Zoom Phone" = Licensed users who ALSO have Phone.
  const [baseSel, setBaseSel] = useState<Set<BaseFacet>>(new Set());
  const [addonSel, setAddonSel] = useState<Set<AddonFacet>>(new Set());

  const baseType = users.data?.base_type ?? null;

  // Only offer chips for facets that actually occur among the loaded rows.
  const presentBases = useMemo(() => {
    const seen = new Set<BaseFacet>((users.data?.users ?? []).map(baseFacet));
    return BASE_FACETS.filter((f) => seen.has(f));
  }, [users.data]);
  const presentAddons = useMemo(() => {
    const seen = new Set<AddonFacet>((users.data?.users ?? []).flatMap(shownAddons));
    return ADDON_FACETS.filter((f) => seen.has(f));
  }, [users.data]);

  const filtered = useMemo(() => {
    const rows = users.data?.users ?? [];
    const q = filter.trim().toLowerCase();
    const matched = rows.filter((u) => {
      if (
        q &&
        !(
          u.email.toLowerCase().includes(q) ||
          (u.display_name ?? "").toLowerCase().includes(q) ||
          (u.first_name ?? "").toLowerCase().includes(q) ||
          (u.last_name ?? "").toLowerCase().includes(q)
        )
      )
        return false;
      if (baseSel.size > 0 && !baseSel.has(baseFacet(u))) return false;
      if (addonSel.size > 0 && !shownAddons(u).some((a) => addonSel.has(a))) return false;
      return true;
    });
    // Default order: Role (Owner → Admin → Custom → Member), then Email A–Z.
    return [...matched].sort(
      (a, b) => roleRank(a.role_id) - roleRank(b.role_id) || a.email.localeCompare(b.email),
    );
  }, [users.data, filter, baseSel, addonSel]);

  const baseChipLabel = (f: BaseFacet): string =>
    f === "basic"
      ? t("customer.users.licenseBasic")
      : f === "licensed"
        ? planName(baseType)
        : f === "none"
          ? t("customer.users.licenseNone")
          : "—";
  const anyLicenseFilter = baseSel.size > 0 || addonSel.size > 0;
  const clearLicense = () => {
    setBaseSel(new Set());
    setAddonSel(new Set());
  };

  const highlightRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (deepLinkedEmail && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [deepLinkedEmail, filtered, sub]);

  const isDeepLinked = (email: string) =>
    !!deepLinkedEmail && email.toLowerCase() === deepLinkedEmail.toLowerCase();

  const pendingColumns: Column<CustomerPendingUser>[] = [
    {
      key: "email",
      header: t("customer.users.email"),
      cell: (r) => (
        <span
          className={cn(
            "truncate font-medium",
            isDeepLinked(r.email) ? "text-(--brand-strong)" : "text-(--text)",
          )}
        >
          {r.email}
          {isDeepLinked(r.email) && (
            <Badge tone="brand" className="ml-2">
              {t("customer.users.fromSearch")}
            </Badge>
          )}
        </span>
      ),
      sortValue: (r) => r.email.toLowerCase(),
    },
    {
      key: "license",
      header: t("customer.users.license"),
      cell: (r) => pendingLicense(t, r.user_type),
      sortValue: (r) => r.user_type,
    },
    {
      key: "invited_at",
      header: t("customer.users.invited"),
      cell: (r) => formatInstant(r.invited_at),
      sortValue: (r) => r.invited_at,
    },
  ];

  const columns: Column<CustomerUser>[] = [
    {
      key: "email",
      header: t("customer.users.email"),
      cell: (r) => (
        <span className="flex flex-col gap-1">
          <span
            className={cn(
              "truncate font-medium",
              r.email === deepLinkedEmail ? "text-(--brand-strong)" : "text-(--text)",
            )}
          >
            {r.email}
            {r.email === deepLinkedEmail && (
              <Badge tone="brand" className="ml-2">
                {t("customer.users.fromSearch")}
              </Badge>
            )}
          </span>
          <LoginTypeIcons types={r.login_types} />
        </span>
      ),
      sortValue: (r) => r.email,
    },
    {
      key: "first_name",
      header: t("customer.users.firstName"),
      cell: (r) => r.first_name ?? "—",
      sortValue: (r) => r.first_name,
    },
    {
      key: "last_name",
      header: t("customer.users.lastName"),
      cell: (r) => r.last_name ?? "—",
      sortValue: (r) => r.last_name,
    },
    {
      key: "role",
      header: t("customer.users.role"),
      cell: (r) => roleLabel(t, r.role_id),
      sortValue: (r) => roleRank(r.role_id), // sort by the fixed Owner/Admin/Custom/Member order
    },
    {
      key: "license",
      header: t("customer.users.license"),
      cell: (r) => {
        const lines = licenseLines(t, r, baseType);
        return (
          <span className="flex flex-col gap-0.5">
            <span className="text-(--text)">{lines[0]}</span>
            {lines.slice(1).map((l) => (
              <span key={l} className="text-(--text-muted)">
                {l}
              </span>
            ))}
          </span>
        );
      },
      // Sort by the base license, then by add-on count so the most-provisioned users group together.
      sortValue: (r) => {
        const lines = licenseLines(t, r, baseType);
        return `${lines[0]} ${String(lines.length).padStart(2, "0")}`;
      },
    },
    {
      key: "last_login_time",
      header: t("customer.users.lastLogin"),
      cell: (r) => formatInstant(r.last_login_time),
      sortValue: (r) => r.last_login_time,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Section title={t("customer.tabs.users")}>
        <SyncNowControl
          label={t("action.syncUsers")}
          fetcher={() => api.syncUsers(accountId)}
          onOk={refreshUsers}
          lastSyncedAt={users.data?.users_synced_at ?? null}
        />
        {/* Cadence-neutral on purpose: the roster is a scheduled snapshot (ZPCD's nightly users run), so
            the copy must not name a specific day. */}
        <FeedNote>{t("customer.users.rosterCadenceNote")}</FeedNote>
      </Section>

      {users.isPending ? (
        <SkeletonRows rows={6} />
      ) : users.isError ? (
        <ErrorState error={users.error} onRetry={() => void users.refetch()} />
      ) : (
        <div className="flex flex-col gap-3">
          <Tabs
            items={[
              {
                value: "users",
                // The API's roster count (all active members, invitees excluded) — not the rows returned.
                label: t("customer.users.tabUsers", { total: formatInt(users.data.count) }),
              },
              { value: "pending", label: pendingTabLabel },
            ]}
            value={sub}
            onValueChange={(v) => setSub(v as SubTab)}
            label={t("customer.users.subTabsLabel")}
          />
          <TabPanel
            id="customer-users"
            value="users"
            active={sub === "users"}
            className="flex flex-col gap-3"
          >
            <RosterSearch
              value={filter}
              onChange={setFilter}
              placeholder={t("customer.users.filterPlaceholder")}
            />

            {presentBases.length + presentAddons.length > 1 && (
              <div
                className="flex flex-wrap items-center gap-1.5"
                role="group"
                aria-label={t("customer.users.filterByLicense")}
              >
                <span className="text-(length:--text-2xs) font-medium text-(--text-subtle)">
                  {t("customer.users.filterByLicense")}
                </span>
                {presentBases.map((f) => (
                  <FilterChip
                    key={f}
                    label={baseChipLabel(f)}
                    active={baseSel.has(f)}
                    onToggle={() => setBaseSel((s) => toggleSet(s, f))}
                  />
                ))}
                {presentBases.length > 0 && presentAddons.length > 0 && (
                  <span className="mx-0.5 h-3.5 w-px bg-(--border)" aria-hidden />
                )}
                {presentAddons.map((f) => (
                  <FilterChip
                    key={f}
                    label={t(ADDON_LABEL_KEY[f])}
                    active={addonSel.has(f)}
                    onToggle={() => setAddonSel((s) => toggleSet(s, f))}
                  />
                ))}
                {anyLicenseFilter && (
                  <button
                    type="button"
                    onClick={clearLicense}
                    className="ml-0.5 rounded-(--radius-sm) px-1.5 py-0.5 text-(length:--text-2xs) font-medium text-(--text-muted) underline-offset-2 hover:text-(--text) hover:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--brand)"
                  >
                    {t("customer.users.clearLicenseFilter")}
                  </button>
                )}
              </div>
            )}

            {users.data.truncated && (
              <FeedNote>
                {t("customer.users.truncatedNote", {
                  returned: formatInt(users.data.returned),
                  total: formatInt(users.data.count),
                })}
              </FeedNote>
            )}

            <Table
              columns={columns}
              rows={filtered}
              rowKey={(r) => r.email}
              caption={t("customer.tabs.users")}
              rowRef={(r) => (r.email === deepLinkedEmail ? highlightRef : undefined)}
              rowClassName={(r) =>
                r.email === deepLinkedEmail ? "bg-(--brand-subtle)/40" : undefined
              }
              empty={
                <EmptyState
                  title={t("customer.users.noMatch")}
                  body={t("customer.users.noMatchBody", { term: filter })}
                />
              }
            />
          </TabPanel>
          <TabPanel
            id="customer-users"
            value="pending"
            active={sub === "pending"}
            className="flex flex-col gap-3"
          >
            <FeedNote>{t("customer.users.pendingNote")}</FeedNote>
            {pendingLoaded ? (
              <>
                <RosterSearch
                  value={pendingFilter}
                  onChange={setPendingFilter}
                  placeholder={t("customer.users.filterPendingPlaceholder")}
                />
                <Table
                  columns={pendingColumns}
                  rows={pendingShown}
                  rowKey={(r) => r.email}
                  caption={pendingTabLabel}
                  rowRef={(r) => (isDeepLinked(r.email) ? highlightRef : undefined)}
                  rowClassName={(r) =>
                    isDeepLinked(r.email) ? "bg-(--brand-subtle)/40" : undefined
                  }
                  empty={
                    pending.length === 0 ? (
                      <EmptyState
                        title={t("customer.users.noPending")}
                        body={t("customer.users.noPendingBody")}
                      />
                    ) : (
                      // Invitees exist, the query matched none of them — not "no invitations".
                      <EmptyState
                        title={t("customer.users.noPendingMatch")}
                        body={t("customer.users.noPendingMatchBody", { term: pendingFilter })}
                      />
                    )
                  }
                />
              </>
            ) : (
              <EmptyState
                title={t("customer.users.pendingNotLoaded")}
                body={t("customer.users.pendingNotLoadedBody")}
              />
            )}
          </TabPanel>
        </div>
      )}
    </div>
  );
}
